// The round of a published sourcing event (Lane 1331, Phase 2 slice 2D): the
// round number, the due time, open or closed, and the buyer's round actions.
// Every action that reaches suppliers goes through a review screen first, and
// sends the offers digest and the feedback digests from that review, so the
// server refuses it if anything changed while the screen was open.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FeedbackBody, formatDueAt } from '../sourcing-portal/portalView';
import { STALE_MESSAGE, formatDate } from './sourcingApi';
import { viewerZone } from './useSourcingChat';
import { DraftFeedbackForm, FeedbackEditor } from './FeedbackDrafts';

// The buyer's time zone, sent with a due time: supplier notices name the time in the
// event's zone (Lane 1393).
const zoneInput = () => (viewerZone() ? { time_zone: viewerZone() } : {});

const MIN_LEAD_MS = 5 * 60 * 1000;

// Refusals that mean the screen is out of date: say so, then reload.
const RELOAD_CODES = ['offers_changed', 'feedback_changed', 'feedback_wrong_round', 'stale_revision', 'event_not_editable',
  'disclosure_not_draft', 'disclosure_not_found', 'supplier_not_found'];

export function roundErrorText(e) {
  switch (e.code) {
    case 'offers_changed': return 'The offers changed while this screen was open. Review again.';
    case 'feedback_changed': return 'A feedback draft changed. Review again.';
    case 'stale_revision': return STALE_MESSAGE;
    case 'due_not_later': return 'The new due time must be later than the current one and at least 5 minutes from now. To end the round early, close it.';
    case 'due_in_past': return 'The due time must be at least 5 minutes from now.';
    case 'supplier_not_in_round': return 'Feedback can go only to a supplier in this round (or, when you open the next round, a supplier you tick for it).';
    case 'feedback_wrong_round': return 'A feedback draft is from an earlier round and cannot be sent. Discard it below, under Feedback to suppliers.';
    case 'too_much_feedback': return 'A supplier can get at most 10 feedback items per round. Combine some of them.';
    case 'event_not_editable': return 'The event changed and this step no longer applies. We reloaded it.';
    case 'disclosure_not_draft': return 'Some feedback was already sent or discarded. Review again.';
    case 'disclosure_not_found': return 'Some feedback no longer exists. Review again.';
    case 'supplier_not_found': return 'A supplier is no longer in this event. Review again.';
    default: return e.message;
  }
}

// A time as the value of a datetime-local input, in the viewer's time zone.
export function toLocalInput(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// A datetime-local value: the Date, or null when it cannot be read.
export function readLocal(text) {
  if (!text) return null;
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
}

// A day as YYYY-MM-DD in the viewer's time zone (plans are calendar days).
export function localDay(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function plural(n, one, many) {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

function responseText(r) {
  if (!r) return 'No response yet';
  switch (r.response) {
    case 'submitted': return `Submitted ${formatDueAt(r.last_submitted_at)}`;
    case 'draft_saved': return 'Draft saved, not submitted';
    case 'viewed': return 'Opened, no quote yet';
    case 'declined': return 'Declined';
    default: return 'Not opened';
  }
}

function emailsText(r) {
  const n = Number(r.emails_sent || 0);
  return n > 0 ? ` ${plural(n, 'email', 'emails')} sent.` : '';
}

// One feedback draft exactly as its supplier will read it.
function DraftPreview({ draft, currency, checked, disabled, onToggle, disabledText }) {
  return (
    <div className={`srce-invsup${disabled ? ' srce-tr--out' : ''}`}>
      <label className="srce-inline">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} />
        <span className="srce-strong">Send this feedback to {draft.supplier_name}</span>
        {draft.source === 'chat' && <span className="srce-tag">Drafted in chat</span>}
      </label>
      {disabled && disabledText && <div className="srce-mut">{disabledText}</div>}
      <div className="srce-preview-box">
        <span className="srce-label">What {draft.supplier_name} will read</span>
        <FeedbackBody item={draft.supplier_view} currency={currency} />
      </div>
    </div>
  );
}

function ExtendForm({ review, busy, onConfirm, onCancel }) {
  const [dueLocal, setDueLocal] = useState(() => toLocalInput(review.due_at));
  const due = readLocal(dueLocal);
  const current = review.due_at ? new Date(review.due_at) : null;
  let problem = '';
  if (!due) problem = 'Choose the new due time.';
  else if (current && due.getTime() <= current.getTime()) problem = 'Choose a time later than the current due time.';
  else if (due.getTime() <= Date.now() + MIN_LEAD_MS) problem = 'The due time must be at least 5 minutes from now.';
  return (
    <section className="srce-panel" aria-label="Extend the due time">
      <h2 className="srce-h2 srce-h2--flush">Extend the due time</h2>
      <div className="srce-mut">Now due {formatDueAt(review.due_at)}. You can only move it later. To end the round early, close it.</div>
      <div className="srce-field">
        <label className="srce-label" htmlFor="srce-round-extend">New due time</label>
        <input id="srce-round-extend" className="srce-input" type="datetime-local" value={dueLocal}
          min={toLocalInput(new Date(Date.now() + MIN_LEAD_MS))} onChange={(e) => setDueLocal(e.target.value)} />
        <span className="srce-mut srce-hint">In your time zone. Suppliers in the round are told the new time.</span>
      </div>
      <div className="srce-actions">
        <button type="button" className="srce-btn srce-btn--primary" disabled={busy || Boolean(problem)}
          onClick={() => onConfirm(due)}>{busy ? 'Saving…' : 'Extend the due time'}</button>
        <button type="button" className="ll-clr" disabled={busy} onClick={onCancel}>Cancel</button>
        {problem && dueLocal && <span className="srce-mut">{problem}</span>}
      </div>
    </section>
  );
}

function CloseReview({ review, busy, onConfirm, onCancel }) {
  const participants = review.suppliers.filter((s) => s.in_current_round);
  // The server says who submitted in THIS round (an offer carried from an
  // earlier round still counts, but is not a new quote).
  const submitted = participants.filter((s) => s.submitted_this_round).length;
  return (
    <section className="srce-panel" aria-label="Close the round">
      <h2 className="srce-h2 srce-h2--flush">Close round {review.current_round}</h2>
      <>
          <div>
            {submitted.toLocaleString()} of {plural(participants.length, 'supplier', 'suppliers')} in this round
            {' '}{submitted === 1 ? 'has' : 'have'} submitted a quote in this round.
            {review.current_round > 1 ? ' Offers from earlier rounds that were not changed still count.' : ''}
          </div>
          <ul className="srce-plainlist">
            {participants.map((s) => (
              <li key={s.supplier_id}><span className="srce-strong">{s.supplier_name}</span>: {responseText(s.response ? s : null)}</li>
            ))}
          </ul>
      </>
      <div className="srce-warnbox">After you close the round, suppliers cannot change their quotes for it. Suppliers in the round are told it closed.</div>
      <div className="srce-actions">
        <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={onConfirm}>
          {busy ? 'Closing…' : `Close round ${review.current_round}`}</button>
        <button type="button" className="ll-clr" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

function ReleaseReview({ review, currency, busy, onConfirm, onCancel }) {
  const [chosen, setChosen] = useState(() => new Set(review.drafts.map((d) => d.disclosure_id)));
  const picked = review.drafts.filter((d) => chosen.has(d.disclosure_id));
  const supplierCount = new Set(picked.map((d) => d.supplier_id)).size;
  const toggle = (id, on) => setChosen((c) => {
    const next = new Set(c);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  return (
    <section className="srce-panel" aria-label="Send feedback">
      <h2 className="srce-h2 srce-h2--flush">Send feedback</h2>
      <div className="srce-mut">
        Each supplier sees only its own feedback. Suppliers can read it at once and answer it in the next round.
        Sent feedback never changes.
      </div>
      {review.drafts.length === 0 && <div className="srce-mut">There is no feedback draft to send.</div>}
      {review.drafts.map((d) => (
        <DraftPreview key={d.disclosure_id} draft={d} currency={currency} checked={chosen.has(d.disclosure_id)}
          onToggle={(on) => toggle(d.disclosure_id, on)} />
      ))}
      <div className="srce-actions">
        <button type="button" className="srce-btn srce-btn--primary" disabled={busy || picked.length === 0}
          onClick={() => onConfirm(picked)}>
          {busy ? 'Sending…' : `Send feedback to ${plural(supplierCount, 'supplier', 'suppliers')}`}</button>
        <button type="button" className="ll-clr" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

function plannedStart(review) {
  const day = review.next_planned_due_date ? String(review.next_planned_due_date).slice(0, 10) : '';
  if (!day) return { value: '', note: '' };
  const shown = formatDate(day);
  if (review.due_at && day <= localDay(review.closed_at || review.due_at)) {
    return { value: '', note: `The planned date (${shown}) is not after this round. Enter a new due time.` };
  }
  const start = new Date(`${day}T17:00`);
  if (Number.isNaN(start.getTime()) || start.getTime() <= Date.now() + MIN_LEAD_MS) {
    return { value: '', note: `The planned date (${shown}) has passed. Enter a new due time.` };
  }
  return { value: `${day}T17:00`, note: `Filled in from the plan (${shown}).` };
}

function OpenRoundReview({ review, currency, busy, onConfirm, onCancel }) {
  const next = review.current_round + 1;
  const [ticked, setTicked] = useState(() => new Set(
    review.suppliers.filter((s) => s.status === 'invited').map((s) => s.supplier_id)));
  const [chosen, setChosen] = useState(() => new Set(review.drafts
    .filter((d) => review.suppliers.some((s) => s.supplier_id === d.supplier_id && s.status === 'invited'))
    .map((d) => d.disclosure_id)));
  // The planned date pre-fills at 17:00 local (UI redesign plan 11.1), unless it
  // has passed or is not after this round; the buyer still confirms the time.
  const plan = plannedStart(review);
  const [dueLocal, setDueLocal] = useState(plan.value);
  const due = readLocal(dueLocal);
  const picked = review.drafts.filter((d) => chosen.has(d.disclosure_id) && ticked.has(d.supplier_id));
  let problem = '';
  if (ticked.size === 0) problem = 'Tick at least one supplier.';
  else if (!due) problem = 'Choose when quotes are due.';
  else if (due.getTime() <= Date.now() + MIN_LEAD_MS) problem = 'The due time must be at least 5 minutes from now.';

  const toggleSupplier = (id, on) => {
    setTicked((t) => {
      const n = new Set(t);
      if (on) n.add(id); else n.delete(id);
      return n;
    });
    if (!on) {
      setChosen((c) => {
        const n = new Set(c);
        review.drafts.filter((d) => d.supplier_id === id).forEach((d) => n.delete(d.disclosure_id));
        return n;
      });
    }
  };
  const toggleDraft = (id, on) => setChosen((c) => {
    const n = new Set(c);
    if (on) n.add(id); else n.delete(id);
    return n;
  });

  return (
    <section className="srce-panel" aria-label={`Open round ${next}`}>
      <h2 className="srce-h2 srce-h2--flush">Open round {next}</h2>
      <div className="srce-mut">
        The parts stay the same. Each supplier keeps its current offer on every part it does not change.
      </div>
      <div className="srce-field">
        <span className="srce-label">Suppliers in round {next}</span>
        {review.suppliers.length === 0 && <div className="srce-mut">No supplier is taking part.</div>}
        {review.suppliers.map((s) => (
          <label key={s.supplier_id} className="srce-inline">
            <input type="checkbox" checked={ticked.has(s.supplier_id)} onChange={(e) => toggleSupplier(s.supplier_id, e.target.checked)} />
            <span className="srce-strong">{s.supplier_name}</span>
            {s.status === 'declined' && <span className="srce-mut">Declined. Tick to ask again.</span>}
            {s.status === 'invited' && !s.in_current_round && <span className="srce-mut">Not in round {review.current_round}.</span>}
          </label>
        ))}
        <span className="srce-mut srce-hint">Suppliers you leave out keep their earlier offers but cannot change them.</span>
      </div>
      <div className="srce-field">
        <label className="srce-label" htmlFor="srce-round-open-due">Quotes due</label>
        <input id="srce-round-open-due" className="srce-input" type="datetime-local" value={dueLocal}
          min={toLocalInput(new Date(Date.now() + MIN_LEAD_MS))} onChange={(e) => setDueLocal(e.target.value)} />
        <span className="srce-mut srce-hint">In your time zone. Suppliers see it in theirs.{plan.note ? ` ${plan.note}` : ''}</span>
      </div>
      <div className="srce-field">
        <span className="srce-label">Feedback sent with it</span>
        {review.drafts.length === 0 && <div className="srce-mut">There is no feedback draft. You can write one below, under Feedback to suppliers.</div>}
        {review.drafts.map((d) => (
          <DraftPreview key={d.disclosure_id} draft={d} currency={currency}
            checked={chosen.has(d.disclosure_id) && ticked.has(d.supplier_id)} disabled={!ticked.has(d.supplier_id)}
            disabledText={`Tick ${d.supplier_name} above to send this feedback.`}
            onToggle={(on) => toggleDraft(d.disclosure_id, on)} />
        ))}
      </div>
      <div className="srce-actions">
        <button type="button" className="srce-btn srce-btn--primary" disabled={busy || Boolean(problem)}
          onClick={() => onConfirm({ due, supplierIds: [...ticked], drafts: picked })}>
          {busy ? 'Opening…' : `Open round ${next} for ${plural(ticked.size, 'supplier', 'suppliers')}`}</button>
        <button type="button" className="ll-clr" disabled={busy} onClick={onCancel}>Cancel</button>
        {problem && <span className="srce-mut">{problem}</span>}
      </div>
    </section>
  );
}

// Feedback to suppliers (moved here from the Offers tab's summary in Lane 1395, Ryan's
// QA): write a draft (by hand or from a supplier's bid), edit or discard it, and see what
// was sent. Sending stays the round's own step above.
function FeedbackSection({ api, eventId, summary, refreshKey, onChanged }) {
  const [items, setItems] = useState(null);
  const [scope, setScope] = useState(null);
  const [draftCurrency, setDraftCurrency] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [drafting, setDrafting] = useState('');
  const [error, setError] = useState('');
  const editable = ['open', 'closed'].includes(summary.status);
  const load = useCallback(() => Promise.all([
    api.disclosures(eventId).then((r) => { setItems(r.items || []); setScope(r.scope_version || null); setDraftCurrency(r.currency || null); }),
    api.responses(eventId).then((r) => setSuppliers(r.items || [])),
  ]).then(() => setError('')).catch((e) => setError(e.message)), [api, eventId]);
  useEffect(() => { load(); }, [load, summary.revision, refreshKey]);
  const changed = async () => { await load(); if (onChanged) await onChanged(); };
  const picked = suppliers.find((x) => String(x.supplier_id) === drafting);
  const currency = draftCurrency || (summary.terms && summary.terms.currency) || 'USD';
  return (
    <section className="srce-panel srce-gap" aria-label="Feedback to suppliers">
      {error && <div className="srce-error" role="alert">{error}</div>}
      {editable && suppliers.length > 0 && (
        <div className="srce-actions">
          <label htmlFor="fb-draft-for" className="srce-strong">Draft feedback from a supplier's bid</label>
          <select id="fb-draft-for" className="srce-input" value={drafting} onChange={(e) => setDrafting(e.target.value)}>
            <option value="">Pick a supplier…</option>
            {suppliers.map((x) => <option key={x.supplier_id} value={String(x.supplier_id)}>{x.supplier_name}</option>)}
          </select>
        </div>
      )}
      {picked && (
        <DraftFeedbackForm key={picked.supplier_id} api={api} eventId={eventId} supplier={picked}
          onDone={async () => { setDrafting(''); await changed(); }} onCancel={() => setDrafting('')} />
      )}
      <FeedbackEditor api={api} eventId={eventId} items={items} currency={currency} editable={editable}
        currentRound={summary.current_round} scopeVersion={scope}
        suppliers={suppliers.map((x) => ({ supplier_id: x.supplier_id, supplier_name: x.supplier_name }))}
        onChanged={changed} />
    </section>
  );
}

// `startMode` ({ mode, at }): a step chosen on the Overview's round choices (Lane 1362),
// opened once with its own confirmation as soon as the round is loaded.
export default function RoundTab({ api, eventId, summary, onChanged, refreshKey, startMode }) {
  const [review, setReview] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const currency = (summary.terms && summary.terms.currency) || 'USD';

  // One request: the statuses shown and the digests sent come from the same
  // server snapshot, so the buyer never confirms over an offer not shown.
  const load = useCallback(() => api.roundReview(eventId)
    .then((r) => { setReview(r); setLoadError(''); })
    .catch((e) => setLoadError(e.message)), [api, eventId]);
  // summary.revision: a change made elsewhere reloads the round. A chat draft
  // does not move the revision, so the workspace's refreshKey reloads it too.
  useEffect(() => { load(); }, [load, summary.revision, refreshKey]);
  const started = useRef(null);
  useEffect(() => {
    if (!review || !startMode || started.current === startMode.at) return;
    started.current = startMode.at;
    // Only a step the round allows now: a round closed meanwhile shows its own choices.
    if (review.status === 'open' && ['close', 'extend'].includes(startMode.mode)) setMode(startMode.mode);
  }, [review, startMode]);

  // Each review starts from fresh digests.
  const openReview = async (next) => {
    setError('');
    setDone('');
    // The fresh review first, then the form (it starts from the review's plan).
    await load();
    setMode(next);
  };

  const run = async (action, input, successText) => {
    setBusy(true);
    setError('');
    setDone('');
    try {
      const r = await api.roundAction(eventId, action, input, review.revision);
      setMode(null);
      setDone(`${successText(r.result || {})}${emailsText(r)}`);
      try { await onChanged(); await load(); } catch (e) { /* the screen reloads on the next change */ }
    } catch (e) {
      setError(roundErrorText(e));
      if (RELOAD_CODES.includes(e.code)) {
        try { await onChanged(); await load(); } catch (err) { /* keep the message */ }
      }
    } finally {
      setBusy(false);
    }
  };

  if (loadError && !review) return <div className="srce-error" role="alert">{loadError}</div>;
  if (!review) return <div className="ll-loading">Loading the round…</div>;

  const isOpen = review.status === 'open';
  const isClosed = review.status === 'closed';
  const participants = review.suppliers.filter((s) => s.in_current_round);
  const pastDue = isOpen && review.due_at && new Date(review.due_at).getTime() <= Date.now();

  return (
    <div>
      {loadError && <div className="srce-error" role="alert">{loadError}</div>}
      <section className="srce-panel" aria-label="The current round">
        <div className="srce-actions">
          <h2 className="srce-h2 srce-h2--flush">Round {review.current_round}</h2>
          <span className={`srce-status srce-status--${isOpen ? 'open' : isClosed ? 'draft' : review.status}`}>
            {isOpen ? 'Open for quotes' : isClosed ? 'Closed' : review.status === 'cancelled' ? 'Cancelled' : 'Awarded'}
          </span>
        </div>
        <div className="srce-times">
          {review.due_at && <span><span className="srce-k">{review.closed_at ? 'Was due' : 'Due'}</span>{formatDueAt(review.due_at)}</span>}
          {review.closed_at && <span><span className="srce-k">Closed</span>{formatDueAt(review.closed_at)}</span>}
          <span><span className="srce-k">Suppliers in this round</span>{participants.length ? participants.map((s) => s.supplier_name).join(', ') : 'None'}</span>
          <span><span className="srce-k">Feedback drafts</span>{review.drafts.length ? `${review.drafts.length.toLocaleString()} not sent yet` : 'None'}</span>
        </div>
        {pastDue && <div className="srce-mut">The due time has passed, so suppliers can no longer send quotes. Close the round to review them, or extend the due time.</div>}
        {isOpen && (
          <div className="srce-actions">
            <button type="button" className="srce-btn" disabled={busy} onClick={() => openReview('extend')}>Extend due time</button>
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={() => openReview('close')}>Close round</button>
          </div>
        )}
        {isClosed && (
          <div className="srce-actions">
            <button type="button" className="srce-btn" disabled={busy || review.drafts.length === 0}
              onClick={() => openReview('release')}>Send feedback</button>
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={() => openReview('open')}>Open next round</button>
            {review.drafts.length === 0 && <span className="srce-mut">No feedback to send yet.</span>}
          </div>
        )}
        {!isOpen && !isClosed && <div className="srce-mut">This event can no longer change.</div>}
      </section>

      {done && <div className="srce-good srce-gap" role="status">{done}</div>}
      {error && <div className="srce-error" role="alert">{error}</div>}

      {mode === 'extend' && isOpen && (
        <ExtendForm key={review.due_at} review={review} busy={busy} onCancel={() => setMode(null)}
          onConfirm={(due) => run('extend_due_date', { due_at: due.toISOString(), ...zoneInput() },
            (r) => `The due time is now ${formatDueAt(r.due_at)}. Suppliers in the round were told.`)} />
      )}
      {mode === 'close' && isOpen && (
        <CloseReview review={review} busy={busy} onCancel={() => setMode(null)}
          onConfirm={() => run('close_round', { expected_offers_digest: review.offers_digest },
            (r) => `Round ${r.round || review.current_round} is closed. Suppliers can no longer change their quotes for it.`)} />
      )}
      {mode === 'release' && isClosed && (
        <ReleaseReview key={review.offers_digest + review.drafts.map((d) => d.content_digest).join('')}
          review={review} currency={currency} busy={busy} onCancel={() => setMode(null)}
          onConfirm={(picked) => {
            const count = new Set(picked.map((d) => d.supplier_id)).size;
            return run('release_disclosure', {
              expected_offers_digest: review.offers_digest,
              disclosures: picked.map((d) => ({ disclosure_id: d.disclosure_id, content_digest: d.content_digest })),
            }, () => `Feedback sent to ${plural(count, 'supplier', 'suppliers')}.`);
          }} />
      )}
      {mode === 'open' && isClosed && (
        // The plan is part of the key: a schedule changed while the form is open
        // starts it again from the new plan, never from the old one (Codex s3 r1 #1).
        <OpenRoundReview key={[review.offers_digest, ...review.drafts.map((d) => d.content_digest),
          review.current_round, review.next_planned_due_date || '', review.closed_at || ''].join('|')}
          review={review} currency={currency} busy={busy} onCancel={() => setMode(null)}
          onConfirm={({ due, supplierIds, drafts }) => run('open_round', {
            expected_offers_digest: review.offers_digest,
            due_at: due.toISOString(),
            ...zoneInput(),
            supplier_ids: supplierIds,
            release: drafts.map((d) => ({ disclosure_id: d.disclosure_id, content_digest: d.content_digest })),
          }, (r) => {
            const n = (r.suppliers || supplierIds).length;
            const sent = (r.released || []).length;
            return `Round ${r.round} is open for ${plural(n, 'supplier', 'suppliers')} until ${formatDueAt(r.due_at)}.`
              + (sent ? ` ${plural(sent, 'feedback draft', 'feedback drafts')} sent with it.` : '');
          })} />
      )}
      {/* `done` changes after every round step, so feedback just sent shows as sent. */}
      <FeedbackSection api={api} eventId={eventId} summary={summary} refreshKey={`${refreshKey}|${done}`}
        onChanged={async () => { try { await load(); } catch (e) { /* the round reloads on the next change */ } }} />
    </div>
  );
}
